import { captureException } from 'marketing-site/lib/sentry'
import MarketoFormOverridePropsContext from 'marketing-site/src/components/context/MarketoFormOverridePropsContext'
import { useMarketoForms } from 'marketing-site/src/components/context/MarketoFormsContext'
import { IProps } from 'marketing-site/src/library/components/MarketoFormV2'
import React, { useContext, useEffect } from 'react'
import { Form, IFormValues } from '../../elements/Form'
import { incrementMetric, recordTimingMetric } from './utils/metrics'
import { submit } from './utils/submission'
import transformMarketoFields from './utils/transformFields/transformMarketoFields'
import useLocaleCode from 'marketing-site/src/library/hooks/useLocaleCode'
import { localiseURL } from 'marketing-site/src/library/utils/localiseURL'
import { handleSkippedSubmissionErrors } from 'marketing-site/src/library/components/MarketoFormV2/utils/handleSkippedSubmissionErrors'
import useTranslate from '../../hooks/useTranslate'
import { IResponse } from 'marketing-site/pages/api/forms/submit'
import {
  IOnMarketoFormSubmit,
  IMarketoFormValues,
} from 'marketing-site/src/mvp/library/elements/MarketoForm'
import { EmailSubmissionResult } from '@intercom/gtm-js/dist/types/email_submission_result'

export const MarketoFormV2 = (props: IProps): React.ReactElement => {
  const localeCode = useLocaleCode()
  const redirectUserTo = (url: string) => window.location.assign(localiseURL(url, localeCode))

  const t0 = typeof window !== 'undefined' ? window.performance.now() : 0

  const {
    onSubmit: onSubmitOverrideProps,
    mergeSubmit = false,
    ...overrideProps
  } = useContext(MarketoFormOverridePropsContext)
  const {
    formId,
    formLeadId,
    defaults,
    submitButtonLabel,
    redirectUrl,
    headingSize,
    headingText,
    subheadingSize,
    subheadingText,
    backgroundColor,
    theme,
    hideLabels,
    forceBusinessEmail,
    emailSubmissionSource,
    hiddenFieldIds,
    icon,
    secondaryCTA,
    formFields,
    isMultiStepSubmit,
    extraSubmitValues,
    waitForSubmit,
  } = { ...props, ...overrideProps }

  const mergeSubmitFn = (submit?: IOnMarketoFormSubmit, overrideSubmit?: IOnMarketoFormSubmit) => {
    return (
      response: IResponse,
      submittedValues?: IMarketoFormValues,
      emailSubmissionResult?: EmailSubmissionResult | null,
    ) => {
      overrideSubmit && overrideSubmit(response, submittedValues, emailSubmissionResult)
      submit && submit(response, submittedValues, emailSubmissionResult)
    }
  }
  const onSubmit = mergeSubmit
    ? mergeSubmitFn(props.onSubmit, onSubmitOverrideProps)
    : onSubmitOverrideProps
    ? onSubmitOverrideProps
    : props.onSubmit

  const marketoForms = useMarketoForms()
  const formData = marketoForms.find((f) => f.id === formId)
  const { translate } = useTranslate()

  useEffect(() => {
    if (!formData) {
      incrementMetric('Marketo.formRender.error', formId)
    } else {
      if (t0 > 0) {
        const t1 = window.performance.now()
        recordTimingMetric('Marketo.renderForm', t1 - t0, formId)
      }

      incrementMetric('Marketo.formRendered', formId)
    }
  }, [])

  if (!formData) {
    captureException(`Tried to load form with id ${formId} but it wasn't found`)
    return <p>{translate("We can't load this form.")}</p>
  }

  return (
    <Form
      fields={transformMarketoFields(
        formFields || formData.fields,
        forceBusinessEmail,
        hideLabels,
        hiddenFieldIds,
      )}
      defaults={defaults}
      submitButtonLabel={submitButtonLabel}
      secondaryCTA={secondaryCTA}
      icon={icon}
      headingText={headingText}
      headingSize={headingSize}
      subheadingText={subheadingText}
      subheadingSize={subheadingSize}
      backgroundColor={backgroundColor}
      theme={theme || 'marketo'}
      onSubmit={async (values: IFormValues | undefined) => {
        let formValues = values
        if (extraSubmitValues) {
          formValues = { ...extraSubmitValues, ...values }
        }

        const { result, emailSubmissionResult } = await submit({
          formId,
          formFields: formValues ?? {},
          emailSubmissionSource,
          formLeadId,
          waitForSubmit,
          bypassValidateFormStructure: isMultiStepSubmit,
        })

        if (onSubmit) {
          onSubmit(result, values, emailSubmissionResult)
        }

        if (!result.success) {
          handleSkippedSubmissionErrors(result.result)
          return result
        }

        if (redirectUrl) {
          redirectUserTo(redirectUrl)
        }

        return result
      }}
    />
  )
}
